import { Injectable } from '@angular/core';
import * as BABYLON from 'babylonjs';
import * as GUI from 'babylonjs-gui';

@Injectable({
  providedIn: 'root'
})
export class SceneSetupService {

  // Thêm method này vào service hiện có
  createGameScene(canvas: HTMLCanvasElement): { engine: BABYLON.Engine, scene: BABYLON.Scene } {
    canvas.style.width = '100%';
    canvas.style.height = '100%';

    const engine = new BABYLON.Engine(canvas, true);
    const scene = new BABYLON.Scene(engine);

    this.createLight(scene);

    return { engine, scene };
  }

  public setupBackground(scene: BABYLON.Scene): void {
    // 1. Gradient nền mặc định
    const gradientTexture = new BABYLON.DynamicTexture("gradientTexture", { width: 1024, height: 1024 }, scene);
    const ctx = gradientTexture.getContext();

    const gradient = ctx.createLinearGradient(0, 0, 0, 1024);
    gradient.addColorStop(0, "#1e3c72");
    gradient.addColorStop(1, "#2a5298");

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 1024, 1024);
    gradientTexture.update();

    const gradientLayer = new BABYLON.Layer("gradientLayer", null, scene, true);
    gradientLayer.texture = gradientTexture;
    gradientLayer.isBackground = true;

    // 2. Chọn ảnh nền từ assets
    const isLandscape = window.innerWidth > window.innerHeight;
    const backgroundImageURL = isLandscape
      ? '/assets/backgroundW.jpg'
      : '/assets/backgroundH.jpg';

    // 3. Tạo layer ảnh nền, ban đầu mờ
    const imageLayer = new BABYLON.Layer("imageLayer", backgroundImageURL, scene, true);
    imageLayer.isBackground = true;
    imageLayer.texture.level = 0; // giống như alpha

    // 4. Fade-in khi ảnh đã tải
    const fadeIn = () => {
      scene.onBeforeRenderObservable.add(() => {
        if (imageLayer.texture.level < 1) {
          imageLayer.texture.level += 0.01;
        }
      });
    };

    // 5. Giả lập "onLoadObservable" bằng cách đợi một chút rồi fade
    const waitForTextureToLoad = () => {
      const checkLoaded = setInterval(() => {
        if (imageLayer.texture.isReady()) {
          clearInterval(checkLoaded);
          fadeIn();
        }
      }, 100);
    };

    waitForTextureToLoad();

    // 6. Optional: cập nhật khi resize màn hình
    const updateImageOnResize = () => {
      const isLandscape = window.innerWidth > window.innerHeight;
      const newURL = isLandscape
        ? '/assets/backgroundW.jpg'
        : '/assets/backgroundH.jpg';

      // Tạo lại layer mới vì không thể update URL
      const newLayer = new BABYLON.Layer("imageLayer", newURL, scene, true);
      newLayer.isBackground = true;
      newLayer.texture.level = 0;

      const checkLoaded = setInterval(() => {
        if (newLayer.texture.isReady()) {
          clearInterval(checkLoaded);
          scene.layers = scene.layers.filter(layer => layer.name !== "imageLayer");
          scene.layers.push(newLayer);
          scene.onBeforeRenderObservable.add(() => {
            if (newLayer.texture.level < 1) {
              newLayer.texture.level += 0.01;
            }
          });
        }
      }, 100);
    };

    window.addEventListener("resize", updateImageOnResize);
  }


  public createCamera(scene: BABYLON.Scene): BABYLON.Camera {
    const camera = new BABYLON.ArcRotateCamera(
      'camera',
      Math.PI / 2,
      Math.PI / 2,
      10,
      new BABYLON.Vector3(0, 0, 0),
      scene
    );

    camera.allowUpsideDown = false;
    camera.lowerBetaLimit = Math.PI / 2 - Math.PI;
    camera.upperBetaLimit = Math.PI / 2 - Math.PI / 30;
    camera.lowerRadiusLimit = 10;
    camera.upperRadiusLimit = 10;
    camera.attachControl(scene.getEngine().getRenderingCanvas(), true);

    return camera;
  }

  // Tạo camera theo dõi player cho gifter scene
  public createFirstPersonCamera(
    scene: BABYLON.Scene,
    canvas: HTMLCanvasElement,
    player?: BABYLON.Mesh
  ): BABYLON.Camera {
    const camera = new BABYLON.ArcRotateCamera(
      'gifterCamera',
      -Math.PI / 2, // alpha
      Math.PI / 3,  // beta (góc nhìn từ trên xuống)
      10,           // radius
      new BABYLON.Vector3(0, 0, 0), // target
      scene
    );

    // Thiết lập giới hạn camera
    camera.lowerBetaLimit = Math.PI / 6;  // Không nhìn quá cao
    camera.upperBetaLimit = Math.PI / 2;  // Không nhìn quá thấp
    camera.lowerRadiusLimit = 5;          // Không zoom quá gần
    camera.upperRadiusLimit = 20;         // Không zoom quá xa

    if (player) {
      camera.setTarget(player.position);

      // Cập nhật target camera khi player di chuyển
      scene.registerBeforeRender(() => {
        if (player && camera) {
          camera.setTarget(player.position);
        }
      });
    }

    camera.attachControl(canvas, true);
    return camera;
  }

  public createHomePlayer(scene: BABYLON.Scene, onPlayerClick?: () => void): BABYLON.Mesh {
    const player = BABYLON.MeshBuilder.CreateSphere('player', { diameter: 0.35 }, scene);
    player.position = new BABYLON.Vector3(0, 1, 0);

    // Tạo material cho home player
    const playerMaterial = new BABYLON.StandardMaterial('homePlayerMaterial', scene);
    playerMaterial.diffuseColor = new BABYLON.Color3(0.6, 0.7, 1);
    player.material = playerMaterial;

    // Thêm click handler nếu có callback
    if (onPlayerClick) {
      player.actionManager = new BABYLON.ActionManager(scene);
      player.actionManager.registerAction(
        new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPickTrigger, () => {
          console.log('Home player clicked');
          onPlayerClick();
        })
      );
    }

    return player;
  }

  public createGifterPlayer(scene: BABYLON.Scene): BABYLON.Mesh {
    const player = BABYLON.MeshBuilder.CreateSphere('player', { diameter: 0.5 }, scene);
    player.position.y = 0.35;

    // Tạo material cho gifter player với màu đặc biệt để user biết có thể click
    const playerMaterial = new BABYLON.StandardMaterial('gifterPlayerMaterial', scene);
    playerMaterial.diffuseColor = new BABYLON.Color3(1, 0.5, 0); // Màu cam để nổi bật
    playerMaterial.specularColor = new BABYLON.Color3(0.2, 0.2, 0.2);
    player.material = playerMaterial;

    return player;
  }

  private addActionToMesh(mesh: BABYLON.Mesh, action: () => void): void {
    mesh.actionManager = new BABYLON.ActionManager(mesh.getScene());
    mesh.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPickTrigger, action)
    );
  }

  createLight(scene: BABYLON.Scene): void {
    const light = new BABYLON.HemisphericLight('light', new BABYLON.Vector3(1, 1, 1), scene);
    light.intensity = 0.45; // Điều chỉnh độ sáng của ánh sáng
  }

  createBalls(
    scene: BABYLON.Scene,
    randomProblem: any,
    pickedProblems: Set<string>,
    onPickCallback: (option: number) => void
  ): BABYLON.Mesh[] {
    const balls: BABYLON.Mesh[] = [];
    for (let option = 1; option <= 5; option++) {
      const ball = BABYLON.MeshBuilder.CreateCapsule(`capsule_${option}`, {
        radius: 0.3,       // Bán kính phần tròn của con nhộng
        height: 1.5,       // Chiều dài của con nhộng
        subdivisions: 4,   // Độ mịn của hình
        tessellation: 16   // Số mặt trong mỗi phần
      }, scene);

      ball.rotation = new BABYLON.Vector3(0, 0, Math.PI / 2);

      // Vị trí ban đầu và góc xoay
      const spacing = 0.8; // Khoảng cách giữa các đối tượng dọc
      const initialX = -4; // Vị trí lề phải ban đầu
      const initialY = -2; // Vị trí y ban đầu (vẫn ở dưới mặt đất)

      ball.position = new BABYLON.Vector3(
        initialX, // X là cố định, căn vào lề phải
        initialY + (option - 1) * spacing, // Y thay đổi để xếp theo chiều dọc
        0 // Z bằng 0 để tất cả các đối tượng nằm trên mặt phẳng
      );

      ball.metadata = {
        name: randomProblem.name,
        option,
        tag: randomProblem.tag,
      };

      // Màu sắc
      const colors = {
        1: BABYLON.Color3.FromHexString('#008000'),
        2: BABYLON.Color3.FromHexString('#00FFFF'),
        3: BABYLON.Color3.FromHexString('#FFFF00'),
        4: BABYLON.Color3.FromHexString('#FFA500'),
        5: BABYLON.Color3.FromHexString('#FF0000'),
      };

      const material = new BABYLON.StandardMaterial('coinMaterial', scene);
      material.diffuseColor = colors[option];
      ball.material = material;

      // Tạo nhãn
      const advancedTexture = GUI.AdvancedDynamicTexture.CreateFullscreenUI('UI');
      const label = new GUI.TextBlock();
      label.text = randomProblem.name;
      label.color = 'white';
      label.fontSize = 20;
      advancedTexture.addControl(label);
      label.linkWithMesh(ball);
      ball.metadata.label = label;

      // Hiệu ứng trồi lên
      this.animateBallAppearance(ball);

      // Xử lý khi click vào bóng
      ball.actionManager = new BABYLON.ActionManager(scene);
      ball.actionManager.registerAction(
        new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPickTrigger, () => {
          onPickCallback(option);
        })
      );

      balls.push(ball);
    }

    return balls;
  }

  // Hiệu ứng di chuyển từ lề ra
  animateBallAppearance(ball: BABYLON.Mesh): void {
    const animation = new BABYLON.Animation(
      'moveFromSide',
      'position.x', // Thay đổi vị trí theo trục X
      30, // Tốc độ khung hình
      BABYLON.Animation.ANIMATIONTYPE_FLOAT,
      BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
    );

    const startPositionX = -4; // Bắt đầu từ ngoài màn hình bên trái (hoặc tùy chỉnh giá trị)
    const endPositionX = -1; // Vị trí kết thúc ở lề phải (hoặc giá trị mong muốn)

    const keys = [
      { frame: 0, value: startPositionX }, // Bắt đầu từ ngoài màn hình (trái)
      { frame: 30, value: endPositionX },  // Kết thúc ở vị trí mong muốn (lề phải)
    ];

    animation.setKeys(keys);
    ball.animations = [animation];
    ball.getScene().beginAnimation(ball, 0, 30, false);
  }

  // Hiệu ứng fade out và xóa
  fadeOutAndDispose(ball: BABYLON.Mesh): void {
    const fadeAnimation = new BABYLON.Animation(
      'fadeOut',
      'visibility',
      30,
      BABYLON.Animation.ANIMATIONTYPE_FLOAT,
      BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
    );

    const keys = [
      { frame: 0, value: 1 }, // Hiện rõ
      { frame: 30, value: 0 }, // Biến mất
    ];

    fadeAnimation.setKeys(keys);
    ball.animations = [fadeAnimation];

    ball.getScene().beginAnimation(ball, 0, 30, false, 1, () => {
      if (ball.metadata?.label) {
        ball.metadata.label.dispose();
      }
      ball.dispose();
    });
  }

  // Sửa lại method này để trả về object thay vì array
  public createFamilyHealthRoom(scene: BABYLON.Scene, userData: any): { gifterMeshes: BABYLON.Mesh[], floor: BABYLON.Mesh } {
    // Tính số ngày lâu nhất kể từ lastSent của các gifter
    let maxDays = 1;
    if (userData?.gifters?.length) {
      maxDays = Math.max(
        ...userData.gifters
          .map((g: any) => {
            if (!g.lastSent) return 0;
            const sent = new Date(g.lastSent);
            const now = new Date();
            const diff = Math.floor((now.getTime() - sent.getTime()) / (1000 * 60 * 60 * 24));
            return diff > 0 ? diff : 0;
          })
      );
    }
    // Quy đổi ra đường kính (mỗi ngày = 0.5, min 6, max 30)
    const diameter = Math.min(Math.max(maxDays * 0.5, 6), 30);

    // Tạo mặt phẳng tròn
    const floor = BABYLON.MeshBuilder.CreateCylinder('floor', {
      diameter: diameter,
      height: 0.1,
      tessellation: 64
    }, scene);
    floor.position.y = 0.05;
    floor.rotation.x = 0;

    // Tạo gradient giống skybox
    const size = 1024;
    const gradientTexture = new BABYLON.DynamicTexture("floorGradient", { width: size, height: size }, scene, false);
    const ctx = gradientTexture.getContext();
    const grd = ctx.createLinearGradient(0, 0, 0, size);
    grd.addColorStop(0, "#6dd5ed");
    grd.addColorStop(0.5, "#2193b0");
    grd.addColorStop(1, "#f7797d");
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, size, size);
    gradientTexture.update();

    const floorMat = new BABYLON.StandardMaterial('floorMat', scene);
    floorMat.diffuseTexture = gradientTexture;
    floorMat.backFaceCulling = false;
    floorMat.emissiveTexture = gradientTexture;
    floorMat.emissiveColor = new BABYLON.Color3(1, 1, 1);
    floor.material = floorMat;
    floor.checkCollisions = true;

    // Ánh sáng dịu
    const light = new BABYLON.HemisphericLight('roomLight', new BABYLON.Vector3(0, 1, 0), scene);
    light.intensity = 1.1;

    // Hiệu ứng lung linh: ánh sáng trắng nhẹ
    const glow = new BABYLON.GlowLayer('glow', scene);
    glow.intensity = 0.4;

    // Cho phép va chạm với sàn
    floor.checkCollisions = true;

    // --- Tạo các hình cầu gifter ---
    const gifterMeshes: BABYLON.Mesh[] = [];
    if (userData?.gifters?.length) {
      const radius = diameter / 2 - 1; // Trừ 1 để không đặt quá sát mép
      userData.gifters.forEach((gifter: any, idx: number) => {
        // Góc ngẫu nhiên trên đường tròn
        const angle = Math.random() * 2 * Math.PI;
        // Vị trí trên đường tròn
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;
        // Tạo hình cầu
        const sphere = BABYLON.MeshBuilder.CreateSphere(`gifter_${idx}`, { diameter: 0.5 }, scene);
        sphere.position = new BABYLON.Vector3(x, 0.35, z);
        sphere.checkCollisions = true;

        // Màu random
        const mat = new BABYLON.StandardMaterial(`gifterMat_${idx}`, scene);
        mat.diffuseColor = new BABYLON.Color3(Math.random(), Math.random(), Math.random());
        sphere.material = mat;
        sphere.metadata = { gifter }; // Lưu thông tin gifter vào metadata
        gifterMeshes.push(sphere);
      });
    }

    // Trả về object chứa cả gifterMeshes và floor
    return { gifterMeshes, floor };
  }

  public createGradientSkybox(scene: BABYLON.Scene): void {
    const size = 1024;
    const gradientTexture = new BABYLON.DynamicTexture("gradient", { width: size, height: size }, scene, false);
    const ctx = gradientTexture.getContext();
    const grd = ctx.createLinearGradient(0, 0, 0, size);
    grd.addColorStop(0, "#6dd5ed");   // Màu trên cùng (xanh nhạt)
    grd.addColorStop(0.5, "#2193b0"); // Màu giữa (xanh đậm)
    grd.addColorStop(1, "#f7797d");   // Màu dưới cùng (hồng cam)
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, size, size);
    gradientTexture.update();

    // Tạo skybox
    const skybox = BABYLON.MeshBuilder.CreateBox("skyBox", { size: 1000 }, scene);
    const skyboxMaterial = new BABYLON.StandardMaterial("skyBoxMaterial", scene);
    skyboxMaterial.diffuseTexture = gradientTexture;
    skyboxMaterial.diffuseTexture.hasAlpha = false;
    skyboxMaterial.backFaceCulling = false;
    skyboxMaterial.disableLighting = true;
    skyboxMaterial.emissiveTexture = gradientTexture; // Thêm dòng này để luôn sáng
    skyboxMaterial.emissiveColor = new BABYLON.Color3(1, 1, 1); // Đảm bảo texture phát sáng
    skybox.material = skyboxMaterial;
    skybox.isPickable = false;
    skybox.infiniteDistance = true;
  }
}
